import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import Footer from '../components/Footer';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';

const emptyForm = {
  nom: '',
  peppol_raison_sociale: '',
  adresse: '',
  code_postal: '',
  ville: '',
  pays: 'Belgique',
  numero_tva: '',
  endpoint_scheme: '',
  endpoint_id: '',
  numerotation_par_resident: true,
  sort_order: 0,
};

function MaisonsRepos() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const canEdit = user?.role === 'admin' || user?.role === 'superadmin';

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/maisons-repos', { credentials: 'include' });
      if (!res.ok) throw new Error('Chargement impossible');
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des maisons de repos.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canEdit) {
      setLoading(false);
      return;
    }
    load();
  }, [canEdit, load]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, sort_order: rows.length });
    setDialogOpen(true);
    setMessage({ type: '', text: '' });
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      nom: row.nom || '',
      peppol_raison_sociale: row.peppol_raison_sociale || '',
      adresse: row.adresse || '',
      code_postal: row.code_postal || '',
      ville: row.ville || '',
      pays: row.pays || 'Belgique',
      numero_tva: row.numero_tva || '',
      endpoint_scheme: row.endpoint_scheme || '',
      endpoint_id: row.endpoint_id || '',
      numerotation_par_resident: Number(row.numerotation_par_resident) === 1,
      sort_order: row.sort_order ?? 0,
    });
    setDialogOpen(true);
    setMessage({ type: '', text: '' });
  };

  const handleChange = (key) => (e) => {
    const v = e.target.value;
    setForm((prev) => ({ ...prev, [key]: v }));
  };

  const handleSave = async () => {
    if (!form.nom.trim()) {
      setMessage({ type: 'warning', text: 'Le nom affiché (liste clients / factures) est obligatoire.' });
      return;
    }
    setSaving(true);
    setMessage({ type: '', text: '' });
    const payload = {
      ...form,
      nom: form.nom.trim(),
      numerotation_par_resident: form.numerotation_par_resident ? 1 : 0,
      sort_order: parseInt(form.sort_order, 10) || 0,
    };
    try {
      const url = editingId ? `/api/maisons-repos/${editingId}` : '/api/maisons-repos';
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const raw = await res.text();
      let body = {};
      try {
        body = raw ? JSON.parse(raw) : {};
      } catch {
        body = {};
      }
      if (!res.ok) {
        const hint =
          res.status === 401
            ? 'Session expirée — reconnectez-vous.'
            : res.status === 403
              ? 'Réservé aux comptes administrateur (ou superadmin).'
              : null;
        throw new Error(body.error || hint || `Erreur ${res.status}${raw && !body.error ? ` : ${raw.slice(0, 120)}` : ''}`);
      }
      setMessage({
        type: 'success',
        text: editingId ? 'Maison mise à jour.' : 'Maison ajoutée. Elle apparaît dans les listes et pour la facturation PEPPOL groupée.',
      });
      setDialogOpen(false);
      await load();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Erreur' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    setSaving(true);
    try {
      const res = await fetch(`/api/maisons-repos/${row.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Suppression refusée');
      setMessage({ type: 'success', text: 'Maison supprimée.' });
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Erreur' });
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit) {
    return (
      <Box sx={{ pb: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Réservé aux administrateurs.
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/')}>
          Retour
        </Button>
        <Footer />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 8 }}>
      <Typography variant="h4" gutterBottom>
        Maisons de repos
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Ajoutez ou retirez des établissements. Le <strong>nom</strong> est celui utilisé sur les clients et les factures.
        Renseignez les champs PEPPOL pour la facturation électronique groupée (destinataire = l’établissement).
      </Typography>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Ajouter une maison
        </Button>
      </Box>

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nom</TableCell>
              <TableCell>Raison sociale PEPPOL</TableCell>
              <TableCell>Numéro TVA</TableCell>
              <TableCell>1 facture / résident</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color="text.secondary">Aucune maison — cliquez sur « Ajouter ».</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.nom}</TableCell>
                  <TableCell>{r.peppol_raison_sociale || '—'}</TableCell>
                  <TableCell>{r.numero_tva || '—'}</TableCell>
                  <TableCell>{Number(r.numerotation_par_resident) === 1 ? 'Oui' : 'Non'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Modifier">
                      <IconButton size="small" onClick={() => openEdit(r)} color="primary">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(r)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Modifier la maison' : 'Nouvelle maison de repos'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                required
                label="Nom (liste clients & factures)"
                value={form.nom}
                onChange={handleChange('nom')}
                helperText="Doit correspondre exactement au choix sur les factures."
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Ordre d’affichage"
                value={form.sort_order}
                onChange={handleChange('sort_order')}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.numerotation_par_resident}
                    onChange={(e) => setForm((p) => ({ ...p, numerotation_par_resident: e.target.checked }))}
                  />
                }
                label="Numérotation par résident (une facture par client le même jour, ex. Les Charmilles)"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Destinataire PEPPOL (établissement)
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Raison sociale (PEPPOL)"
                value={form.peppol_raison_sociale}
                onChange={handleChange('peppol_raison_sociale')}
                placeholder="Si vide, le nom ci-dessus est utilisé"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Numéro de TVA" value={form.numero_tva} onChange={handleChange('numero_tva')} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Adresse"
                value={form.adresse}
                onChange={handleChange('adresse')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Code postal" value={form.code_postal} onChange={handleChange('code_postal')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Ville" value={form.ville} onChange={handleChange('ville')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Pays" value={form.pays} onChange={handleChange('pays')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Schéma PEPPOL (ex. 0208)"
                value={form.endpoint_scheme}
                onChange={handleChange('endpoint_scheme')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Identifiant PEPPOL"
                value={form.endpoint_id}
                onChange={handleChange('endpoint_id')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Annuler
          </Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={22} /> : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => !saving && setDeleteTarget(null)}>
        <DialogTitle>Supprimer « {deleteTarget?.nom} » ?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Impossible si des clients ou factures utilisent encore ce nom. Sinon la fiche sera supprimée.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={saving}>
            Annuler
          </Button>
          <Button color="error" variant="contained" onClick={() => deleteTarget && handleDelete(deleteTarget)} disabled={saving}>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </Box>
  );
}

export default MaisonsRepos;
